import React, {useEffect, useRef, useState, useMemo} from 'react';
import PropTypes from 'prop-types';
import PDFObject from 'pdfobject';
import filesize from 'filesize';
import ClampLines from 'react-clamp-lines';

import { ATTACHMENTS } from 'newJs/config';
import { isPlayable, isViewable } from 'newJs/utils';
import { useAttachsParams } from 'providers/attachs-params';
import { useAttachsState } from 'providers/attachs-state';
import ActionGroup from './ActionGroup';
import AttachDataProvider from 'providers/attachs-data';
import Item from './Component';
import {IconFileExt} from 'components/Icons/list';
import {getIconByExtension} from 'containers/Compose/Attachments/AttachHelpers';
// NOTE: Install these modules and use for testing long attach names
// import rndString from 'randomstring';
// import rndWords from 'random-words';

const { VIEW_MODE } = ATTACHMENTS;

const Attachment = (props) => {
    // console.dir(props);
    // const itemName = rndWords({min: 30, max: 50, join: ' '}) + itemData.name;
    // const itemName = rndString.generate(200) + itemData.name;
    const { children, hasError = false, itemData, ...otherProps } = props;
    const { currentViewMode, setStateValue } = useAttachsState();
    const { browser, handleClick } = useAttachsParams();
    const isMsBrowser = ['msie', 'msedge'].includes(browser.name);
    const item = React.useMemo(() => {
        const { extension: ext = '', mime = '', name = 'noname', type = '' } = itemData;
        const extension = ext.toLowerCase();
        const itemType = ['asice', 'edoc'].includes(extension)
            ? 'document'
            : type.toLowerCase();
        return {
            name,
            extension,
            isCalendar: mime.toLowerCase() === 'text/calendar',
            isImage: itemType.toLowerCase() === 'image',
            isPdf: extension === 'pdf',
            isPlayable: isPlayable(itemData),
            isVCard: extension === 'vcf',
            isViewable: isViewable(itemData),
            isZip: itemData?.zip?.length > 0,
            type: itemType,
        };
    }, [itemData]);
    const fileParts = item.name.split('.');
    const fileName = fileParts.slice(0, -1).join('.');
    const [trimmedText, setTrimmedText] = useState(fileName);
    const textBoxRef = useRef(null);
    const minVisibleLetters = 9; // set min symbols count what are visible (included ...)

    const itemTitleClickHandler = (e) => {
        // console.log(e.target);
        setStateValue('clickedItemData', itemData);

        if (item.isCalendar && !browser.msie) {
            setStateValue('calendarViewMode', true, true);
        }
        else if (item.isViewable && item.isImage) {
            if (!browser.msie) {
                setStateValue('imgViewMode', true, true);
                return;
            }
            handleClick(e, itemData);
        }
        else if (item.isPdf) {
            if (!isMsBrowser) {
                if (PDFObject.supportsPDFs) {
                    setStateValue('pdfViewMode', true, true);
                    return;
                }
            }
            handleClick(e, itemData);
        }
        else if (item.isPlayable) {
            setStateValue('playerMode', true, true);
        }
        else if (item.isZip) {
            setStateValue('zipViewMode', true, true);
        }
        else {
            handleClick(e, itemData);
        }
    };

    // helper to calculate the width of the text using canvas
    const getTextWidth = useMemo(() => {
        return (text, textBox) => {
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            context.font = window.getComputedStyle(textBox).getPropertyValue('font');
            return context.measureText(`${text}${item.extension === 'unknown' ? '' : `.${item.extension}`}`).width;
        };
    }, [item.extension]);

    // This function is made for box, which contains text
    // and need append 3 dots for text if it is resizing and can not fit in the box
    const trimTextToFitBox = useMemo(() => {
        return (text, textBox) => {
            if (!textBox) return '';
            // need remove text length with extension length
            // to show correctly trimmed text with any extension length
            const filenameLength = text.length - item.extension.length;

            // 36.5 = svg width + svg padding + box padding
            let boxWidth = textBox.clientWidth - 36;
            // Check if text fits in the box
            let textWidth = getTextWidth(text, textBox, item);

            // Text fits, return full text
            if (textWidth <= boxWidth) {
                return text;
            }

            // Calculate how many characters can be shown before appending dots
            const visibleChars = Math.max(minVisibleLetters, Math.floor((boxWidth / textWidth) * filenameLength));

            // Calculate the number of characters to show before and after the dots
            const charsBeforeDots = Math.ceil((visibleChars - 3) / 2);
            const charsAfterDots = Math.floor((visibleChars - 3) / 2);

            // Construct trimmed text with dots in the middle
            return text.substr(0, charsBeforeDots) + '...' + text.substr(text.length - charsAfterDots);
        };
    }, [minVisibleLetters, getTextWidth]);

    useEffect(() => {
        const recalculateText = () => {
            setTrimmedText(trimTextToFitBox(fileName, textBoxRef.current));
        };

        // setting trimmed text and also listen user resize
        setTrimmedText(trimTextToFitBox(fileName, textBoxRef.current));
        window.addEventListener('resize', recalculateText);

        return () => {
            window.removeEventListener('resize', recalculateText);
        };
    }, [fileName, textBoxRef, setTrimmedText, trimTextToFitBox]);

    return (
        <Item
            id={`attach_${itemData.itemIdx}`}
            data-hashid={itemData.hashId}
            style={itemData.style || null}
            textBoxRef={textBoxRef}
            {...otherProps}
        >
            {children}
            <Item.Content>
                <Item.Name
                    className={hasError ? null : 'item-action'}
                    title={item.name}
                    onClick={itemTitleClickHandler}
                    typeColor={item.type}
                >
                    {item.isImage && currentViewMode === VIEW_MODE.GRID
                        ? ''
                        : (
                            <span className={`item-extension-icon ${item.type ? 'item-extension-icon--' + item.type.replace(' ','-') : ''}`}>
                                <IconFileExt ext={item.extension} type={getIconByExtension(item.type)}/>
                            </span>
                        )
                    }

                    {hasError && <Item.Icon ext='unknown'/>}
                    {currentViewMode !== VIEW_MODE.GRID ?
                        <span className="item-name__value">{trimmedText}.{item.extension}</span>
                        :
                        <ClampLines
                            buttons={false}
                            className="item-name__value"
                            id={itemData.hashId.slice(0, 6)}
                            lines={5}
                            text={item.name}
                            ellipsis={`...${item.extension}`}
                        />}
                </Item.Name>
                {currentViewMode === VIEW_MODE.GRID && (
                    <span data-test="eml-drid-attach-size" className="item-fsize">
                        {filesize(itemData.size, { round: 2, standard: 'jedec', base: 2 })}
                    </span>
                )}
                <AttachDataProvider value={itemData}>
                    <ActionGroup hasError={hasError} item={item} itemData={itemData} />
                </AttachDataProvider>
            </Item.Content>
        </Item>
    );
};

Attachment.propTypes = {
    children: PropTypes.node,
    hasError: PropTypes.bool,
    itemData: PropTypes.object,
};

export default Attachment;
